import React, { FC, useEffect, useState } from 'react';
import { ContentCard } from '../Cards';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { ApplicationRoutes } from '../../routes/ApplicationRoutes';
import { useApiCall } from '../../hooks/api/apiHooks';
import { registerNewUser, validateRegistrationToken } from '../../apiCalls/users/userApiCalls';
import { Nullable } from '../../types/custom/Nullable';
import { useNotifications } from '../../hooks/notifications/useNotifications';
import { Alert, Button, Col, Form, ListGroup, ProgressBar } from 'react-bootstrap';
import RequiredIndicator  from '../../utils/FormTemplates/RequiredIndicator';
import nextId from 'react-id-generator';
import { FcApproval } from 'react-icons/fc';
import { FaCheck } from 'react-icons/fa';
import { UserRegistration } from '../../types/api/users/UserRegistration';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { TextField, TextFieldProps } from '@material-ui/core';
import './Register.scss';
import { IconContext } from 'react-icons/lib';
import { TokenValidationResponse, TokenValidationResultType } from '../../types/api/users/TokenValidationResponse';
import { useMounted } from '../../hooks/components/useMounted';

/** 
 * This represents the fields that are present on the token that we deserialize directly.
 * This structure is really specific to this component only, is why it's declared here.
 * */
interface DeserializedToken {
    nameFirst: string;
    nameLast: string;
    email: string;
    token: string;
}

const customTextField: FC<TextFieldProps> = (props) => <TextField autoComplete="off" {...props} />;

const Register: FC = () => {
	// Component State
	const dateUtil = new DateFnsUtils();
	const [ userData, setUserData ] = useState<Nullable<DeserializedToken>>(null);
	const [ validationResponse, setValidationResponse ] = useState<Nullable<TokenValidationResponse>>(null);

	const [ password, setPassword ] = useState('');
	const [ passwordConfirm, setPasswordConfirm ] = useState('');
	const [ socialSecurity, setSocialSecurity ] = useState('');
	const [ birthDate, setBirthDate ] = useState<Nullable<MaterialUiPickersDate>>(null);
	const [ showError, setShowError ] = useState(false);

	// Api Calls
	const { apiCall, isLoading: isRegisteringUser } = useApiCall(registerNewUser);

	// Notifications
	const { notifyError } = useNotifications();

	// Routing
	const history = useHistory();
	const location = useLocation();

	const isMounted = useMounted();

	// Logic to decode token when url changes (should only possibly ever be 1 time this gets called per usage of this page.)
	useEffect(() => {
		let newUser: Nullable<DeserializedToken> = null;

		const { search } = location;

		if(search.length) {
			const leadingUrlText = '?token=';
			const b64Token = search.substring(leadingUrlText.length);

			if(b64Token.length) {

				const decodedToken = atob(b64Token);
				const tokenObject = JSON.parse(decodedToken);

				// ToDo: Note: The only reason "passwordStrength" is being attached to the object below is because it's being rendered as a "field" using the form library.
				// If we swap out the form implementation, this can be stripped out and just rendered in a sane way.
				newUser = {
					...tokenObject
				};
			}
		}
		setUserData(newUser);
	}, [location]);

	// Responsible for checking up on the status of the user's token after we've parsed it.
	// Purely a convenience feature at current moment.
	useEffect(() => {
		async function checkTokenStatus(tokenData: DeserializedToken) {
			let validationResult: Nullable<TokenValidationResponse> = null;

			// Note: It's "Okay" for this request to fail. This is purely a convenience feature for the user.
			// Though, that said, it should only ever fail for bad actors at current moment.
			try {
				validationResult = await validateRegistrationToken({
					email: tokenData.email,
					nameFirst: tokenData.nameFirst,
					nameLast: tokenData.nameLast,
					token: tokenData.token
				});
			}
			catch {
				validationResult = null;
			}

			if (isMounted) {
				setValidationResponse(validationResult);
			}
		}

		if(userData !== null) {
			checkTokenStatus(userData);
		}
	}, [userData]);
	
	//password requirements
	const emptyPassword = password.length > 0;
	const greaterOrEqualToEight = password.length >= 8;
	const specialCharacterReqMet = /(?=.*[!@#$%^&*])/.test(password);
	const alphaNumericReq = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/.test(password);
	//form requirements
	const validSocialVerify = socialSecurity.length === 4;
	const validPassword = emptyPassword && greaterOrEqualToEight && specialCharacterReqMet && alphaNumericReq;
	const validPasswordConfirm = validPassword && password === passwordConfirm;
	const validBirthDate = birthDate !== null && dateUtil.isValid(birthDate);

	//#region Progress Bar
	let progress = 0;

	if (emptyPassword) {
		progress += 25;
	}

	if (greaterOrEqualToEight) {
		progress += 25;
	}

	if (specialCharacterReqMet) {
		progress += 25;
	}

	if (alphaNumericReq) {
		progress += 25;
	}
	let variant = 'danger';
	if (progress >= 50) {
		variant = 'warning';
	}

	if (progress >= 75) {
		variant = 'info';
	}

	if (progress >= 100) {
		variant = 'success';
	}

	const handleSocialSecurity = (value: string) => {
		if(value.length >= 4) {
			setSocialSecurity(value.slice(0, 4).replace(/\D/g,''));
		} else {
			setSocialSecurity(value.replace(/\D/g,''));
		}
	};
    
	const handleFormSubmit = async() => {
		if(userData && birthDate !== null && dateUtil.isValid(birthDate)) {
			const formData: UserRegistration = {
				email: userData.email,
				nameFirst: userData.nameFirst,
				nameLast: userData.nameLast,
				password,
				passwordConfirm,
				sSN: socialSecurity,
				dateOfBirth: birthDate.toDateString(),
				token: userData.token
			};
			try {
				await apiCall(formData);
    
				// This is the successful logic branch - navigate to the root url.
				history.push(ApplicationRoutes.applicationRoot);
			}
			catch {
				setPassword('');
				setPasswordConfirm('');
				setShowError(true);
				notifyError('There was a problem with your account creation.');
			}
		}
	};

	const handleBirthDate = (birthDay: MaterialUiPickersDate) => {
		setBirthDate(birthDay);
	};
	
	// Invalid route parameters rendering short circuit =======================================================================================
	const tokenCouldBeParsed = userData !== null;

	// Not sure how this would be possible - we failed to parse the actual token as found in the url. Could be triggered via user URL tampering.
	if (!tokenCouldBeParsed) {
		return (
			<ContentCard>
				<div>
					Invitation may have expired. Please check with your your Group Administrator / Human Resources Team to confirm
				</div>
			</ContentCard>
		);
	}

	// Token validation state short circuits ======================================================================================

	// We know the user has already successfully created a user account via this token. Give them a link into the application.
	if (validationResponse?.validationResultType === TokenValidationResultType.TokenAlreadySuccessfullyCreatedUser) {
		return (
			<ContentCard>
				<div>
					This invitation has already been used to successfully create a user account. Please click <Link to={ApplicationRoutes.applicationRoot}>here</Link> to log in.
				</div>
			</ContentCard>
		);
	}
	// Show a message about checking for a more recent email.
	else if (validationResponse?.validationResultType === TokenValidationResultType.TokenLikelyReplacedWithNewToken) {
		return (
			<ContentCard>
				<div>
					You have opened an invitation that may be expired. Please check that you do not have a more recent invitation email in your inbox.
				</div>
			</ContentCard>
		);
	}


	return (
		<ContentCard loading={isRegisteringUser}>
			{showError && 
			<Alert variant="danger" onClose={() => setShowError(false)}>
				<Alert.Heading>Error occurred.</Alert.Heading>
				<p>
					Information entered may have not matched information on file.
				</p>
				<hr />
				<p className="mb-0">
					If you continue to have problems, please reach out to your Group Administrator / Human Resources Team to confirm your social security number and date of birth are correct in WolfTracs.
				</p>
			</Alert>}
			<Form> 
				<Form.Row>
					<Form.Group as={Col} controlId={nextId()}>
						<Form.Label>First name</Form.Label>
						<Form.Control name="firstName" type="text" value={userData?.nameFirst} readOnly />
					</Form.Group>
					<Form.Group as={Col} controlId={nextId()}>
						<Form.Label>Last name</Form.Label>
						<Form.Control name="lastName" type="text" value={userData?.nameLast} readOnly/>
					</Form.Group>
				</Form.Row>
				<Form.Group controlId={nextId()}>
					<Form.Label>Email</Form.Label>
					<Form.Control name="email" type="email" value={userData?.email} readOnly/>
				</Form.Group>
				<Form.Group  controlId={nextId()}>
					<Form.Label>Last 4 digits of your Social Security Number<RequiredIndicator /></Form.Label>
					<Form.Control name="ssn" type="text" autoComplete="off" value={socialSecurity} onChange={e => handleSocialSecurity(e.currentTarget.value)} isInvalid={socialSecurity.length !== 0 && socialSecurity.length !== 4} placeholder="Last 4 digits" pattern="\d*" />
				</Form.Group>
				<Form.Group controlId={nextId()}>
					<Form.Label className="customLabelDisplay">Date of Birth<RequiredIndicator /></Form.Label>
					<MuiPickersUtilsProvider utils={DateFnsUtils}>
						<KeyboardDatePicker
							disableToolbar
							autoOk
							required
							format="MM/dd/yyyy"
							margin="none"
							label="mm/dd/yyyy"
							value={birthDate}
							onChange={handleBirthDate}
							KeyboardButtonProps={{
								'aria-label': 'change birth date',
							}}
							TextFieldComponent={customTextField}
						/>
						{validBirthDate && <IconContext.Provider value={{ className: 'mt-4' }}><FaCheck size={16} color="#28a745"/></IconContext.Provider>}
					</MuiPickersUtilsProvider>
						
				</Form.Group>
				<Form.Group controlId={nextId()}>
					<Form.Label>Password<RequiredIndicator /></Form.Label>
					<Form.Control name="password" type="password" value={password} isValid={validPassword} isInvalid={emptyPassword && !validPassword} onChange={e => setPassword(e.currentTarget.value)} placeholder="Enter password"/>
					<Form.Control.Feedback type="valid">Valid password</Form.Control.Feedback>
				</Form.Group>
				<ProgressBar striped variant={variant} now={progress} />
				<ListGroup variant="flush">
					<ListGroup.Item>Must have at least 8 Characters in length {greaterOrEqualToEight && <FcApproval />}</ListGroup.Item>
					<ListGroup.Item>Include Lower case(a-z), uppercase(A-Z), and number(0,9) {alphaNumericReq && <FcApproval />}</ListGroup.Item>
					<ListGroup.Item>{'Use at least one of these special Characters(!@#$%^&*)'} {specialCharacterReqMet && <FcApproval />}</ListGroup.Item>
				</ListGroup>
				<Form.Group controlId={nextId()}>
					<Form.Label>Confirm Password<RequiredIndicator /></Form.Label>
					<Form.Control name="passwordConfirm" type="password" placeholder="Enter password same as above" value={passwordConfirm} isValid={validPasswordConfirm} isInvalid={validPassword && !validPasswordConfirm} onChange={e => setPasswordConfirm(e.currentTarget.value)} />
					<Form.Control.Feedback type="invalid">Value must match the password entered above</Form.Control.Feedback>
					<Form.Control.Feedback type="valid">Matching password</Form.Control.Feedback>
				</Form.Group>
				<ListGroup>
					<ListGroup.Item>If you are having trouble registering, please make sure your password is meeting the outlined requirements.</ListGroup.Item>
					<ListGroup.Item>If you continue to have problems, please reach out to your Group Administrator / Human Resources Team to confirm your social security number and date of birth are correct in WolfTracs.</ListGroup.Item>
				</ListGroup>
				<Button disabled={!validPassword|| !validSocialVerify|| !validPasswordConfirm || !validBirthDate} variant="primary" type="submit" className="float-right mt-3" onClick={handleFormSubmit}>
					{isRegisteringUser ? 'Submitting...' : 'Submit'}
				</Button>
			</Form>
		</ContentCard>
	);
};

export default Register;